/**
 * MA-ARCH-136A: Helper Text Component
 * 
 * Provides consistent, precise helper text for War Room sections.
 * Purpose: Improve user understanding + decision clarity (NOT cosmetic)
 */
import React from 'react';
import { cn } from '../../lib/utils';
import {
  Info,
  HelpCircle,
} from 'lucide-react';

/**
 * Standardized helper definitions for War Room concepts
 */
export const HELPER_DEFINITIONS = {
  // Strategy Layer
  profit_paths: "Strategy: message + funnel + script",
  message_variant: "The core idea that triggered the lead",
  
  // Execution Layer
  campaigns: "Traffic source (Meta, Google, etc.)",
  scripts: "What was said during booking/sales",
  journeys: "The path a lead took through your funnel experience",
  
  // Performance Layer
  impulse_alignment: "Compares when scripts reinforce acquisition messaging",
  
  // Decision Layer
  optimization_recommendations: "System-generated actions based on performance data",
  confidence_badge: "Based on data volume and consistency",
};

/**
 * Inline helper text - subtle, non-intrusive
 */
export function HelperText({ 
  text, 
  variant = 'inline', // 'inline' | 'tooltip' | 'block'
  className 
}) {
  if (variant === 'block') {
    return (
      <div className={cn(
        "flex items-start gap-2 text-xs text-slate-400 bg-slate-800/30 rounded px-2.5 py-1.5 border border-slate-700/30",
        className
      )}>
        <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-slate-500" />
        <span>{text}</span>
      </div>
    );
  }
  
  if (variant === 'tooltip') {
    return (
      <span 
        className={cn("inline-flex items-center cursor-help", className)}
        title={text}
      >
        <HelpCircle className="h-3.5 w-3.5 text-slate-500 hover:text-slate-400 transition-colors" />
      </span>
    );
  }
  
  // Default: inline
  return (
    <span className={cn("text-xs text-slate-500 font-normal", className)}>
      {text}
    </span>
  );
}

/**
 * Section header with helper text
 */
export function SectionHeader({ 
  title, 
  helper, 
  icon: Icon,
  layer, // 'strategy' | 'execution' | 'performance' | 'decision'
  className,
  children 
}) {
  const layerColors = {
    strategy: 'border-l-violet-500',
    execution: 'border-l-blue-500',
    performance: 'border-l-emerald-500',
    decision: 'border-l-amber-500',
  };
  
  const layerLabels = {
    strategy: 'Strategy',
    execution: 'Execution',
    performance: 'Performance',
    decision: 'Decision',
  };
  
  return (
    <div className={cn(
      "flex items-center justify-between mb-3",
      layer && `pl-3 border-l-2 ${layerColors[layer]}`,
      className
    )}>
      <div className="flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-slate-400" />}
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-white">{title}</h3>
            {layer && (
              <span className={cn(
                "text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded",
                layer === 'strategy' && "bg-violet-500/20 text-violet-400",
                layer === 'execution' && "bg-blue-500/20 text-blue-400",
                layer === 'performance' && "bg-emerald-500/20 text-emerald-400",
                layer === 'decision' && "bg-amber-500/20 text-amber-400",
              )}>
                {layerLabels[layer]}
              </span>
            )}
          </div>
          {helper && (
            <p className="text-xs text-slate-500 mt-0.5">{helper}</p>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

/**
 * Warning banner for important alerts
 */
export function WarningBanner({ 
  title, 
  message, 
  severity = 'warning', // 'warning' | 'error' | 'info'
  icon: CustomIcon,
  className,
  compact = false,
}) {
  const config = {
    warning: {
      bg: 'bg-amber-500/10 border-amber-500/30',
      text: 'text-amber-300',
      icon: 'text-amber-400',
    },
    error: {
      bg: 'bg-red-500/10 border-red-500/30',
      text: 'text-red-300',
      icon: 'text-red-400',
    },
    info: {
      bg: 'bg-blue-500/10 border-blue-500/30',
      text: 'text-blue-300',
      icon: 'text-blue-400',
    },
  };
  
  const styles = config[severity];
  const Icon = CustomIcon || Info;
  
  if (compact) {
    return (
      <div className={cn(
        "flex items-center gap-2 px-3 py-2 rounded border",
        styles.bg,
        className
      )}>
        <Icon className={cn("h-4 w-4 flex-shrink-0", styles.icon)} />
        <span className={cn("text-xs", styles.text)}>{message}</span>
      </div>
    );
  }
  
  return (
    <div className={cn(
      "flex items-start gap-3 p-3 rounded-lg border",
      styles.bg,
      className
    )}>
      <Icon className={cn("h-5 w-5 mt-0.5 flex-shrink-0", styles.icon)} />
      <div>
        {title && (
          <p className={cn("text-sm font-medium mb-1", styles.text)}>{title}</p>
        )}
        <p className={cn("text-xs", styles.text, "opacity-80")}>{message}</p>
      </div>
    </div>
  );
}

export default {
  HelperText,
  SectionHeader,
  WarningBanner,
  HELPER_DEFINITIONS,
};
